// P-SESSION-CHUNKING-AND-CONTENT-POOL (Part 2 — #27).
//
// Bounded, controlled repetition for under-resourced content pools.
//
// THE BUG (#27): the live item pipelines fill a session of `count`
// items by indexing into a small launch_safe pool. When the pool is
// smaller than the session length they wrapped UNBOUNDED — the for-run
// zip did `pool[idx % pool.length]` and the screens fell back to
// `items[done] ?? items[0]`. With ms.math.05 (1 launch_safe item) or
// ms.read.06 (5) a kid saw the SAME item 13-of-15 times after a wrong
// answer. Alex hit exactly this in real use.
//
// THE FIX: replace unbounded wrap with a hard cap. Each unique item may
// appear at most `maxReps` (= 2) times in a session — "the kid should
// never see the same item more than 2x" (Alex's binding constraint).
// When the pool is too small to fill `count` under the cap, the session
// SHRINKS (the returned array is shorter than `count`); callers derive
// an `effectiveLength` from `items.length` rather than padding with more
// repeats. The real cure is more authored content (Phase H); this bounds
// the damage in the meantime and emits a Sentry breadcrumb so we can see
// which skills are starving.
//
// 2 is a calibration target (Alex's brief: "hard cap of ~2 reps per
// item"), not a cited pedagogy threshold.

export const MAX_REPS_PER_ITEM = 2;

interface BoundedRepetitionResult<T> {
  /** The served sequence. Length is min(count, uniquePool.length *
   *  maxReps) — SHORTER than `count` when the pool can't fill it under
   *  the cap. */
  items: T[];
  /** Count of DISTINCT pool items that appear at least once. */
  uniqueItemsServed: number;
  /** Count of served entries that are repeats (round >= 1). */
  duplicatesServed: number;
}

/**
 * Serve up to `count` items from `uniquePool`, repeating in round-robin
 * order when the pool is smaller than `count`, but never letting any
 * single item appear more than `maxReps` times.
 *
 * Round 0 emits every unique item ONCE, IN ORDER — so an adequate pool
 * (uniques >= count) returns exactly the first `count` items unchanged,
 * preserving the upstream pack order (no visual-baseline shift for the
 * common, well-resourced case). Later rounds re-emit items in the same
 * order, each passed through `markRepetition` so the UI/telemetry can
 * tell it is a repeat. `markRepetition` MUST return a new value (not
 * mutate its argument) so the round-0 originals stay clean.
 *
 * Pure + side-effect-free; unit-tested in
 * src/lib/engine/__tests__/boundedRepetition.test.mjs.
 */
export function withBoundedRepetition<T>(
  uniquePool: T[],
  count: number,
  markRepetition: (item: T) => T,
  maxReps: number = MAX_REPS_PER_ITEM,
): BoundedRepetitionResult<T> {
  const uniques = uniquePool.length;
  if (uniques === 0 || count <= 0) {
    return { items: [], uniqueItemsServed: 0, duplicatesServed: 0 };
  }
  const reps = Math.max(1, maxReps);
  const total = Math.min(count, uniques * reps);
  const items: T[] = [];
  let duplicatesServed = 0;
  for (let i = 0; i < total; i++) {
    const round = Math.floor(i / uniques);
    const item = uniquePool[i % uniques];
    if (round === 0) {
      items.push(item);
    } else {
      items.push(markRepetition(item));
      duplicatesServed += 1;
    }
  }
  return {
    items,
    uniqueItemsServed: Math.min(uniques, total),
    duplicatesServed,
  };
}
